import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Settings, Home, Book, Star, Baby, Heart, Users, Gift, Check, X, ChevronDown, Share } from 'lucide-react';
import { onAuthStateChanged, EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from './firebase';
import { Preferences } from '@capacitor/preferences';
import { App } from '@capacitor/app';
import { signUpWithEmail, signInWithEmail, signInWithGoogle, signOutUser } from './authService';
import { initializeRevenueCat, purchasePremium, restorePurchases, checkPremiumStatus } from './services/revenuecat';
import { trackBookCreated, trackPremiumPurchased } from './analytics';
import { registerPushNotifications, unregisterPushNotifications } from './services/pushNotifications';
import { 
  createBabyBook, 
  getUserBabyBooks, 
  getBabyBook, 
  updateBabyBook, 
  removeUserFromBabyBook, 
  deleteBabyBook,
  getUserProfile,
  updateUserProfile,
  inviteUserToBabyBook,
  getUserInvitations,
  acceptInvitation
} from './babyBookService';
import { createEntry, getBabyBookEntries, createMilestone, getBabyBookMilestones, uploadPhotoFromBase64, updateEntry, deleteEntry } from './entryService';
// DISABLED: import { syncService } from './services/smartsync/SyncService.ts';
import { VoiceRecorder } from 'capacitor-voice-recorder';
import ReviewModal from './components/ReviewModal';
import { PrintBookFlow } from './components/PrintBook';
// App loaded 

// Keep these outside the function
const FREE_TIER_BOOK_LIMIT = 3;
const FREE_TIER_PHOTO_LIMIT = 150;
const FREE_TIER_CONTRIBUTOR_LIMIT = 2;
const PREMIUM_PRICE = '$4.99';

export default function BabyBookin() {
  // 1. ALL STATES MUST BE DECLARED FIRST
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('login');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedReviewPhotos, setSelectedReviewPhotos] = useState([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBase64, setAudioBase64] = useState(null);
  // 2. START SYNC ENGINE ONLY AFTER LOGIN
  useEffect(() => {
    if (user && user.uid) {
      // SmartSync disabled
      
      // This wakes up the scanner only when you are actually in the app
      // DISABLED: syncService.initialize(user.uid).catch(err => {
        // console.error("❌ Sync Engine failed to start:", err);
      // });
    }
  }, [user]); // This 'watches' the user state

  // 2. THE ABSOLUTE PRIORITY CHECK (The Bridge)
  useEffect(() => {
    const handleIncomingPhotos = (ids) => {
      // disabled
      setSelectedReviewPhotos(ids);
      setView('home');
    };

    // Check if there are IDs already waiting in the window
    if (window.pendingPhotoIds && window.pendingPhotoIds.length > 0) {
      handleIncomingPhotos(window.pendingPhotoIds);
      window.pendingPhotoIds = null; // Clear it so it doesn't pop up again
    }

    // Add a listener for any future "shouts" while the app is open
    window.addEventListener('photos-ready-to-review', (e) => {
      if (e.detail && e.detail.photoIds) {
        handleIncomingPhotos(e.detail.photoIds);
      }
    });

    return () => window.removeEventListener('photos-ready-to-review', handleIncomingPhotos);
  }, []);

  // 3. THE EVENT LISTENER (The Shout)
  // 4. VOICE RECORDING LOGIC
  const handleStartRecording = async () => {
    try {
      // Check for microphone permission
      const status = await VoiceRecorder.requestAudioRecordingPermission();
      if (status.value) {
        setIsRecording(true);
        await VoiceRecorder.startRecording();
        console.log("Recording started...");
      }
    } catch (err) {
      console.error("Recording failed to start:", err);
    }
  };

  const handleStopRecording = async () => {
    try {
      setIsRecording(false);
      const result = await VoiceRecorder.stopRecording();
      if (result.value && result.value.recordDataBase64) {
        setAudioBase64(result.value.recordDataBase64);
        console.log("Memory captured! Audio data ready.");
      }
    } catch (err) {
      console.error("Recording failed to stop:", err);
    }
  };
  useEffect(() => {
    // SmartSync bridge disabled
    
    const handleGlobalTap = (event) => {
      const ids = event.detail.photoIds;
      // disabled
      
      // 1. Force the app into the home view so the modal is allowed to render
      if (typeof setView === 'function') {
        setView('home'); 
      }
      
      // 2. Update the modal states
      setSelectedReviewPhotos(ids);
    };

    window.addEventListener('forceReviewModal', handleGlobalTap);
    return () => window.removeEventListener('forceReviewModal', handleGlobalTap);
  }, []);

  // SHARE EXTENSION URL HANDLER

  // ... rest of your component logic (loginForm, signupForm, etc.)

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ email: '', password: '', name: '' });
  
  const [babyBooks, setBabyBooks] = useState([]);
  const [currentBabyBook, setCurrentBabyBook] = useState(null);
  const [babyBookId, setBabyBookId] = useState(null);
  
  const [entries, setEntries] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [newEntry, setNewEntry] = useState({ 
    text: '', 
    milestone: '', 
    milestoneType: '', 
    customMilestoneType: '', 
    customDate: '', 
    customTime: '', 
    photos: [] 
  });
  
  const [showBabyBookSetup, setShowBabyBookSetup] = useState(false);
  const [babyBookStep, setBabyBookStep] = useState(0);
  const [babyBookForm, setBabyBookForm] = useState({
    completed: false,
    babyName: '',
    momName: '',
    dadName: '',
    howTheyMet: '',
    aboutMom: '',
    aboutDad: '',
    howFoundOut: '',
    siblingReactions: '',
    howSharedNews: '',
    pregnancyNotes: '',
    cravings: '',
    nameStory: '',
    babyShower: '',
    birthDate: '',
    birthTime: '',
    hospital: '',
    doctorName: '',
    weight: '',
    length: '',
    siblings: '',
    familyTree: {}
  });
  
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [milestoneModal, setMilestoneModal] = useState({ 
    type: '', 
    customType: '',
    description: '', 
    date: new Date().toISOString().split('T')[0],
    photos: []
  });
  
  const [newFamilyMember, setNewFamilyMember] = useState({ relation: '', name: '' });
  
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'viewer' });
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ 
    current: 0, 
    total: 0, 
    processing: false 
  });

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showBabyBookSelector, setShowBabyBookSelector] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showEditEntryModal, setShowEditEntryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrintBook, setShowPrintBook] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [showShareTip, setShowShareTip] = useState(() => {
    return localStorage.getItem('babybookin_share_tip_dismissed') !== 'true';
  });
  
  const milestoneTypes = [
    'First Bath', 'First Smile', 'First Laugh', 'Sitting Up', 
    'Rolling Over', 'Standing', 'First Steps', 'Walking', 
    'First Words', 'Real Food'
  ];

  const babyBookSteps = [
    {
      title: 'Baby Info',
      icon: Baby,
      fields: [
        { key: 'babyName', label: "Baby's Name", type: 'text' },
        { key: 'birthDate', label: 'Birth Date', type: 'date' },
        { key: 'birthTime', label: 'Birth Time', type: 'time' }
      ]
    },
    {
      title: 'Pre-Birth: Parents',
      icon: Heart,
      fields: [
        { key: 'momName', label: "Mother's Name", type: 'text' },
        { key: 'dadName', label: "Father's Name", type: 'text' },
        { key: 'howTheyMet', label: 'How They Met', type: 'textarea' },
        { key: 'aboutMom', label: 'About Mom', type: 'textarea' },
        { key: 'aboutDad', label: 'About Dad', type: 'textarea' }
      ]
    },
    {
      title: 'Pre-Birth: The News',
      icon: Gift,
      fields: [
        { key: 'howFoundOut', label: 'How We Found Out', type: 'textarea' },
        { key: 'siblingReactions', label: 'Sibling Reactions', type: 'textarea' },
        { key: 'howSharedNews', label: 'Sharing the News', type: 'textarea' }
      ]
    },
    {
      title: 'Pre-Birth: Pregnancy',
      icon: Heart,
      fields: [
        { key: 'pregnancyNotes', label: 'Pregnancy Journey', type: 'textarea' },
        { key: 'cravings', label: 'Cravings & Aversions', type: 'textarea' }
      ]
    },
    {
      title: 'Pre-Birth: Preparation',
      icon: Gift,
      fields: [
        { key: 'nameStory', label: 'How We Picked Your Name', type: 'textarea' },
        { key: 'babyShower', label: 'Baby Shower', type: 'textarea' }
      ]
    },
    {
      title: 'Birth Details',
      icon: Baby,
      fields: [
        { key: 'hospital', label: 'Hospital/Location', type: 'text' },
        { key: 'doctorName', label: 'Doctor/Midwife', type: 'text' },
        { key: 'weight', label: 'Birth Weight', type: 'text' },
        { key: 'length', label: 'Birth Length', type: 'text' },
        { key: 'siblings', label: 'Siblings', type: 'text' }
      ]
    },
    {
      title: 'Family Tree',
      icon: Users,
      isCustom: true
    }
  ];
useEffect(() => {
    let isMounted = true;
    
    // Fallback timeout in case Firebase doesn't initialize
    const timeout = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
        if (!user) { console.log("🔴 TIMEOUT setView login"); setView("login"); };
      }
    }, 5000);
    
    // Initialize RevenueCat anonymously right away (before auth).
    // This allows pre-login purchases to work.
    initializeRevenueCat(null);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!isMounted) return;
      
      if (firebaseUser) {
        clearTimeout(timeout);
        setUser(firebaseUser);
        
        // Load user profile
        const profileResult = await getUserProfile(firebaseUser.uid);
        if (profileResult.success && isMounted) {
          setUserProfile(profileResult.userProfile);
        }
        
        // Identify (log in) with RevenueCat — merges any anonymous purchases
        await initializeRevenueCat(firebaseUser.uid);
        
        // Check premium status from RevenueCat (for native app)
        const rcPremiumStatus = await checkPremiumStatus();
        if (rcPremiumStatus !== null && isMounted) {
          // Update Firebase profile if RevenueCat says premium
          if (rcPremiumStatus && !profileResult?.userProfile?.isPremium) {
            await updateUserProfile(firebaseUser.uid, { isPremium: true });
            setUserProfile(prev => ({ ...prev, isPremium: true }));
          }
        }
        
        await loadUserBabyBooks(firebaseUser.uid, firebaseUser.email);

        // Register for push notifications (native only, non-blocking)
        registerPushNotifications(firebaseUser.uid);

        if (isMounted) setView('home');
      } else {
        setUser(null);
        setUserProfile(null);
        setBabyBooks([]);
        setCurrentBabyBook(null);
        setBabyBookId(null);
        console.log('🔴 AUTH-ELSE setView login'); setView('login');
      }
      if (isMounted) setLoading(false);
    });
    
    return () => {
      isMounted = false;
      clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  // SHARE EXTENSION HANDLER - Listen for photos shared from Photos app
  useEffect(() => {
    const handleSharedPhotos = () => {
      // Share event received
      const photos = window.sharedPhotosFromNative;
      if (photos && photos.length > 0) {
        // Photos received
        window.sharedPhotosFromNative = null; // Clear immediately
        
        // Set photos and navigate - use callback to ensure state is set
        setNewEntry(prev => {
          return { ...prev, photos: photos };
        });
        
        // Small delay to ensure state update completes before view change
        setTimeout(() => {
          setView("add");
        }, 50);
      }
    };

    // Listen for the custom event from native
    window.addEventListener("sharedPhotosReady", handleSharedPhotos);

    // Also check immediately in case photos were already injected
    if (window.sharedPhotosFromNative && window.sharedPhotosFromNative.length > 0) {
      handleSharedPhotos();
    }

    return () => {
      window.removeEventListener("sharedPhotosReady", handleSharedPhotos);
    };
  }, []);
  const leaveSharedBook = async () => {
    if (!currentBabyBook || !user) return;
    
    if (window.confirm(`Are you sure you want to leave "${currentBabyBook.babyName}"? You'll need to be re-invited to access it again.`)) {
      const result = await removeUserFromBabyBook(babyBookId, user.uid);
      if (result.success) {
        alert('You have left the baby book.');
        await loadUserBabyBooks(user.uid, user.email);
        setView('home');
      }
    }
  };

  const removeMember = async (userId) => {
  if (!currentBabyBook) return;
  
  if (window.confirm('Are you sure you want to remove this member?')) {
    const result = await removeUserFromBabyBook(babyBookId, userId);
    if (result.success) {
      // Update the current baby book state by removing the member
      const updatedMembers = { ...currentBabyBook.members };
      delete updatedMembers[userId];
      
      setCurrentBabyBook({
        ...currentBabyBook,
        members: updatedMembers
      });
      
      alert('Member removed successfully');
    }
  }
};
  const loadUserBabyBooks = async (userId, userEmail) => {
    const result = await getUserBabyBooks(userId);
    if (result.success) {
      setBabyBooks(result.babyBooks);

      // Check for pending invitations
      if (userEmail) {
        const inviteResult = await getUserInvitations(userEmail);
        if (inviteResult.success && inviteResult.invitations.length > 0) {
          setPendingInvitations(inviteResult.invitations);
        }
      }

      if (result.babyBooks.length > 0) {
        await selectBabyBook(result.babyBooks[0].id);
      } else {
        setShowBabyBookSetup(true);
      }
    }
  };
  const selectBabyBook = async (bookId) => {
    setBabyBookId(bookId);
    
    const bookResult = await getBabyBook(bookId);
    if (bookResult.success) {
      setCurrentBabyBook(bookResult.babyBook);
      setBabyBookForm(bookResult.babyBook);
      
      // Initialize Smart Sync with baby book context
      // DISABLED: try {
        // await syncService.initialize({
          // babyBookId: bookId,
          // babyBirthDate: bookResult.babyBook.birthDate 
            // ? new Date(bookResult.babyBook.birthDate) 
            // : null,
          // babyName: bookResult.babyBook.babyName || 'Baby',
        // });
        // console.log('[BabyBookin] Smart Sync initialized for', bookResult.babyBook.babyName);
      // } catch (err) {
        // console.warn('[BabyBookin] Smart Sync init failed (may be on web):', err);
      // }
    }
    
    const entriesResult = await getBabyBookEntries(bookId);
    if (entriesResult.success) {
      setEntries(entriesResult.entries);
    }
    
    const milestonesResult = await getBabyBookMilestones(bookId);
    if (milestonesResult.success) {
      setMilestones(milestonesResult.milestones);
    }
  };

  const handleSignup = async () => {
    setErrorMessage('');
    if (!signupForm.name || !signupForm.email || !signupForm.password) {
      setErrorMessage('Please fill in all fields');
      return;
    }
    
    const result = await signUpWithEmail(signupForm.email, signupForm.password, signupForm.name);
    if (!result.success) {
      setErrorMessage(result.error);
    }
  };

  const handleLogin = async () => {
    setErrorMessage('');
    const result = await signInWithEmail(loginForm.email, loginForm.password);
    if (!result.success) {
      setErrorMessage(result.error);
    }
  };

  const handleGoogleSignIn = async () => {
    setErrorMessage('');
    const result = await signInWithGoogle();
    if (!result.success) {
      setErrorMessage(result.error);
    }
  };

  const handleLogout = async () => {
    try {
      await unregisterPushNotifications(user?.uid);
      await signOutUser();
      // Clear all state
      setUser(null);
      setUserProfile(null);
      setBabyBooks([]);
      setCurrentBabyBook(null);
      setBabyBookId(null);
      setEntries([]);
      setMilestones([]);
      console.log('🔴 LOGOUT setView login'); setView('login');
    } catch (error) {
      console.error('Logout error:', error);
      alert('Error signing out. Please try again.');
    }
  };

  const completeBabyBookSetup = async () => {
    if (babyBookId) {
      // Editing existing book
      await updateBabyBook(babyBookId, { ...babyBookForm, completed: true });
      await selectBabyBook(babyBookId);
    } else {
      // Creating new book - check limit
      const ownedBooks = babyBooks.filter(book => book.ownerId === user?.uid);
      if (!userProfile?.isPremium && ownedBooks.length >= FREE_TIER_BOOK_LIMIT) {
        alert(`Free users can have up to ${FREE_TIER_BOOK_LIMIT} baby books. Upgrade to Premium for more!`);
        setShowBabyBookSetup(false);
        return;
      }
      
      const result = await createBabyBook(user.uid, { ...babyBookForm, completed: true });
      if (result.success) {
        trackBookCreated(result.babyBookId);
        // Reload books list and select the newly created book
        const booksResult = await getUserBabyBooks(user.uid);
        if (booksResult.success) {
          setBabyBooks(booksResult.babyBooks);
        }
        // Select the newly created book
        await selectBabyBook(result.babyBookId);
      }
    }
    setShowBabyBookSetup(false);
  };

  const skipBabyBookSetup = async () => {
    if (babyBookStep < babyBookSteps.length - 1) {
      // Not the last step - advance to next
      setBabyBookStep(babyBookStep + 1);
    } else {
      // Last step - complete setup
      await completeBabyBookSetup();
    }
  };
  
  const addEntry = async () => {
    if (!babyBookId) {
      alert('Please create a baby book first');
      return;
    }
    
    // Check photo limit for free users before uploading
    if (!userProfile?.isPremium && newEntry.photos.length > 0) {
      const currentPhotoCount = currentBabyBook?.photoCount || 0;
      const newPhotoCount = currentPhotoCount + newEntry.photos.length;
      
      if (newPhotoCount > FREE_TIER_PHOTO_LIMIT) {
        alert(`Free users can only have ${FREE_TIER_PHOTO_LIMIT} photos. You currently have ${currentPhotoCount} photos. Upgrade to Premium for unlimited photos!`);
        return;
      }
    }
    
    const uploadedPhotoURLs = [];
    for (const photoBase64 of newEntry.photos) {
      const result = await uploadPhotoFromBase64(user.uid, photoBase64);
      if (result.success) {
        uploadedPhotoURLs.push(result.photoURL);
      } 
    }
    let timestamp;
    if (newEntry.customDate) {
      const dateStr = newEntry.customTime 
        ? `${newEntry.customDate}T${newEntry.customTime}`
        : `${newEntry.customDate}T12:00`;
      timestamp = new Date(dateStr).toISOString();
    } else {
      timestamp = new Date().toISOString();
    }
    
    const timePeriod = calculateTimePeriod(timestamp);
    const finalMilestoneType = newEntry.milestoneType === 'Custom' 
      ? newEntry.customMilestoneType 
      : newEntry.milestoneType;
    
    const entryData = {
      timestamp,
      text: newEntry.text,
      milestone: newEntry.milestone,
      milestoneType: finalMilestoneType,
      authorId: user.uid,
      authorName: user.displayName || user.email,
      timePeriod,
      photos: uploadedPhotoURLs
    };
    const entryResult = await createEntry(babyBookId, entryData);
    
    if (entryResult.success) {
      if (newEntry.milestone && finalMilestoneType) {
        await createMilestone(babyBookId, {
          type: finalMilestoneType,
          description: newEntry.milestone,
          date: timestamp,
          photos: uploadedPhotoURLs
        });
      }
      
      await selectBabyBook(babyBookId);
      
      setNewEntry({ 
        text: '', 
        milestone: '', 
        milestoneType: '', 
        customMilestoneType: '', 
        customDate: '', 
        customTime: '', 
        photos: [] 
      });
      setView('home');
    }
  };

  const addMilestoneFromModal = async () => {
    if (milestoneModal.type === 'Custom' && !milestoneModal.customType) {
      alert('Please enter a custom milestone name');
      return;
    }
    if (!milestoneModal.description || !milestoneModal.date) {
      alert('Please fill all fields');
      return;
    }
    
    const finalType = milestoneModal.type === 'Custom' ? milestoneModal.customType : milestoneModal.type;
    const timestamp = new Date(milestoneModal.date + 'T12:00:00').toISOString();
    
    await createMilestone(babyBookId, {
      type: finalType,
      description: milestoneModal.description,
      date: timestamp,
      photos: milestoneModal.photos || []
    });
    
    const timePeriod = calculateTimePeriod(timestamp);
    
    // Check photo limit for free users
    const photoCountToAdd = (milestoneModal.photos || []).length;
    if (!userProfile?.isPremium && photoCountToAdd > 0) {
      const currentPhotoCount = currentBabyBook?.photoCount || 0;
      const newPhotoCount = currentPhotoCount + photoCountToAdd;
      
      if (newPhotoCount > FREE_TIER_PHOTO_LIMIT) {
        alert(`Free accounts are limited to ${FREE_TIER_PHOTO_LIMIT} photos. You have ${currentPhotoCount} photos. Upgrade to Premium for unlimited photos!`);
        setShowUpgradeModal(true);
        return;
      }
    }
    await createEntry(babyBookId, {
      timestamp,
      text: milestoneModal.description,
      milestone: milestoneModal.description,
      milestoneType: finalType,
      authorId: user.uid,
      authorName: user.displayName || user.email,
      timePeriod,
      photos: milestoneModal.photos || []
    });
    
    await selectBabyBook(babyBookId);
    setShowMilestoneModal(false);
    setMilestoneModal({ type: '', customType: '', description: '', date: new Date().toISOString().split('T')[0], photos: [] });
  };

  const handleBulkPhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setBulkUploadProgress({ current: 0, total: files.length, processing: true });
    
    // Check photo limit for free users
    if (!userProfile?.isPremium) {
      const currentPhotoCount = currentBabyBook?.photoCount || 0;
      const newPhotoCount = currentPhotoCount + files.length;
      
      if (newPhotoCount > FREE_TIER_PHOTO_LIMIT) {
        const remaining = FREE_TIER_PHOTO_LIMIT - currentPhotoCount;
        alert(`Free accounts are limited to ${FREE_TIER_PHOTO_LIMIT} photos. You have ${currentPhotoCount} photos and can only upload ${remaining} more. Upgrade to Premium for unlimited photos!`);
        setShowUpgradeModal(true);
        setShowBulkUpload(false);
        return;
      }
    }
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        const photoData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });

        const photoTimestamp = new Date(file.lastModified).toISOString();
        const timePeriod = calculateTimePeriod(photoTimestamp);
        
        const uploadResult = await uploadPhotoFromBase64(user.uid, photoData);
        
        if (uploadResult.success) {
          await createEntry(babyBookId, {
            timestamp: photoTimestamp,
            text: '',
            milestone: '',
            milestoneType: '',
            authorId: user.uid,
            authorName: user.displayName || user.email,
            timePeriod,
            photos: [uploadResult.photoURL]
          });
        }
        
        setBulkUploadProgress({ current: i + 1, total: files.length, processing: true });
      } catch (error) {
        console.error(`Error processing photo ${i + 1}:`, error);
      }
    }
    
    await selectBabyBook(babyBookId);
    setBulkUploadProgress({ current: 0, total: 0, processing: false });
    setShowBulkUpload(false);
    setView('journal');
  };

  const calculateTimePeriod = (timestamp) => {
    if (!currentBabyBook?.birthDate) return 'General';
    
    const birth = new Date(currentBabyBook.birthDate);
    const entry = new Date(timestamp);
    const diff = entry - birth;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'Pre-Birth';
    if (days < 7) return 'First Days';
    if (days < 28) {
      const weeks = Math.floor(days / 7);
      return `Week ${weeks}`;
    }
    
    let months = 0;
    let tempDate = new Date(birth);
    while (tempDate < entry) {
      tempDate.setMonth(tempDate.getMonth() + 1);
      if (tempDate < entry) months++;
      else break;
    }
    
    if (months < 12) return `${months} Month${months !== 1 ? 's' : ''}`;
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (remainingMonths === 0) return `${years} Year${years !== 1 ? 's' : ''}`;
    return `${years} Year${years !== 1 ? 's' : ''} ${remainingMonths} Month${remainingMonths !== 1 ? 's' : ''}`;
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewEntry(prev => ({
          ...prev,
          photos: [...prev.photos, event.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setNewEntry(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleMilestonePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setMilestoneModal(prev => ({
          ...prev,
          photos: [...(prev.photos || []), event.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeMilestonePhoto = (index) => {
    setMilestoneModal(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const getEntriesByPeriod = () => {
    const grouped = {};
    entries.forEach(entry => {
      const period = entry.timePeriod || 'General';
      if (!grouped[period]) grouped[period] = [];
      grouped[period].push(entry);
    });
    return grouped;
  };

  const sortPeriods = (periods) => {
    const order = ['Pre-Birth', 'First Days'];
    const weeks = periods.filter(p => p.startsWith('Week ')).sort((a, b) => {
      const numA = parseInt(a.split(' ')[1]);
      const numB = parseInt(b.split(' ')[1]);
      return numA - numB;
    });
    const months = periods.filter(p => p.includes('Month') && !p.includes('Year')).sort((a, b) => {
      const numA = parseInt(a.split(' ')[0]);
      const numB = parseInt(b.split(' ')[0]);
      return numA - numB;
    });
    const years = periods.filter(p => p.includes('Year')).sort((a, b) => {
      const numA = parseInt(a.split(' ')[0]);
      const numB = parseInt(b.split(' ')[0]);
      return numA - numB;
    });
    const general = periods.filter(p => p === 'General');
    
    return [...order.filter(p => periods.includes(p)), ...weeks, ...months, ...years, ...general];
  };


  // Handle direct URL routing for Terms/Privacy (accessible without login)
  const [publicPage, setPublicPage] = useState(() => { const h = window.location.hash; if (h === "#/terms") return "terms"; if (h === "#/privacy") return "privacy"; return null; });
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === "#/terms") setPublicPage("terms");
    if (hash === "#/privacy") setPublicPage("privacy");
    const handleHashChange = () => {
      const h = window.location.hash;
      if (h === "#/terms") setPublicPage("terms");
      else if (h === "#/privacy") setPublicPage("privacy");
      else setPublicPage(null);
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  if (publicPage === "terms") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean-50 to-sand-50 p-8">
        <div className="bg-white p-8 rounded-xl shadow-md max-w-4xl mx-auto">
          <button onClick={() => setPublicPage(null)} className="text-ocean-600 hover:text-ocean-800 mb-4 inline-block">← Back to BabyBookin</button>
          <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
          <p className="text-sm text-gray-500 mb-6">Last updated: January 2025</p>
          <div className="space-y-6 text-gray-700">
            <section><h2 className="text-xl font-semibold mb-2">1. Acceptance of Terms</h2><p>By accessing or using BabyBookin, you agree to be bound by these Terms of Service. If you do not agree, please do not use our service.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">2. Description of Service</h2><p>BabyBookin is a digital baby book platform that allows you to document your baby's milestones, upload photos, and share memories with family members.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">3. User Accounts</h2><p>You must provide accurate information when creating an account. You are responsible for maintaining the security of your account. You must be at least 18 years old to create an account.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">4. User Content</h2><p>You retain ownership of all content you upload to BabyBookin. By uploading content, you grant us a license to store, display, and process that content to provide our service.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">5. Acceptable Use</h2><p>You agree not to upload illegal, harmful, or inappropriate content, violate any laws, attempt unauthorized access to our systems, or use the service for commercial purposes without permission.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">6. Premium Subscriptions</h2><p>Premium features are available through an auto-renewable subscription at $4.99/month. Payment will be charged to your Apple ID account at confirmation of purchase. Subscriptions automatically renew unless canceled at least 24 hours before the end of the current period. You can manage and cancel subscriptions in your Apple ID account settings. No cancellation of the current subscription is allowed during the active period.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">7. Termination</h2><p>We may suspend or terminate your account if you violate these terms. You may delete your account at any time through Settings.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">8. Disclaimer</h2><p>BabyBookin is provided "as is" without warranties of any kind. We do our best to keep your data safe, but we are not liable for any data loss.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">9. Contact</h2><p>For questions about these Terms, contact us at support@babybookin.com</p></section>
          </div>
        </div>
      </div>
    );
  }

  if (publicPage === "privacy") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean-50 to-sand-50 p-8">
        <div className="bg-white p-8 rounded-xl shadow-md max-w-4xl mx-auto">
          <button onClick={() => setPublicPage(null)} className="text-ocean-600 hover:text-ocean-800 mb-4 inline-block">← Back to BabyBookin</button>
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-6">Last updated: January 2025</p>
          <div className="space-y-6 text-gray-700">
            <section><h2 className="text-xl font-semibold mb-2">1. Information We Collect</h2><p>When you use BabyBookin, we collect information you provide directly, including account information (email address, name), baby book content (entries, photos, milestones, family information), and payment information (processed securely through Stripe).</p></section>
            <section><h2 className="text-xl font-semibold mb-2">2. How We Use Your Information</h2><p>We use your information to provide and maintain the BabyBookin service, send you daily reminders (if you opt in), process payments for premium features, and improve our services.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">3. Data Storage and Security</h2><p>Your data is stored securely using Google Firebase with industry-standard encryption. Photos are stored in Firebase Storage. We implement appropriate security measures to protect your personal information.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">4. Sharing Your Information</h2><p>We do not sell your personal information. We only share your data with family members you explicitly invite to your baby book, with service providers (Firebase, Stripe, SendGrid) to operate our service, and if required by law.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">5. Your Rights</h2><p>You can access and export your data at any time, delete your account and all associated data, opt out of reminder emails, and update your information in Settings.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">6. Children's Privacy</h2><p>BabyBookin is intended for parents and family members to document their children's lives. We do not knowingly collect information directly from children under 13.</p></section>
            <section><h2 className="text-xl font-semibold mb-2">7. Contact Us</h2><p>If you have questions about this Privacy Policy, contact us at support@babybookin.com</p></section>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean-50 to-sand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-ocean-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-ocean-900 font-semibold">Loading BabyBookin...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <><div className="min-h-screen bg-gradient-to-br from-ocean-50 to-sand-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <Baby size={64} className="mx-auto mb-4 text-ocean-600" />
            <h1 className="text-4xl font-bold text-ocean-900 mb-2">BabyBookin</h1>
            <p className="text-gray-600">Your baby's digital memory book</p>
          </div>

          {view === 'login' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-center mb-6">Welcome Back</h2>
              
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {errorMessage}
                </div>
              )}
              
              <input
                type="email"
                placeholder="Email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ocean-500"
              />
              
              <input
                type="password"
                placeholder="Password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ocean-500"
              />
              
              <div className="text-right">
                <button
                  onClick={async () => {
                    if (!loginForm.email) {
                      alert('Please enter your email address');
                      return;
                    }
                    try {
                      await sendPasswordResetEmail(auth, loginForm.email);
                      alert('Password reset email sent! Check your inbox.');
                    } catch (error) {
                      alert('Error: ' + error.message);
                    }
                  }}
                  className="text-sm text-ocean-600 hover:text-ocean-700 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              
              <button
                onClick={handleLogin}
                className="w-full bg-ocean-600 text-white py-3 rounded-lg font-semibold hover:bg-ocean-700"
              >
                Log In
              </button>
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>
              
              {!window.Capacitor && (
              <button
                onClick={handleGoogleSignIn}
                className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>
              )}
              
              <p className="text-center text-gray-600 mt-4">
                Don't have an account?{' '}
                <button onClick={() => setView('signup')} className="text-ocean-600 font-semibold hover:underline">
                  Sign up
                </button>
              </p>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="w-full bg-gradient-to-r from-ocean-500 to-peach-500 text-white py-3 rounded-lg font-semibold hover:from-ocean-600 hover:to-peach-600"
                >
                  View Premium Features
                </button>
                <p className="text-center text-xs text-gray-500 mt-2">
                  Subscribe anytime with your Apple ID
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-center mb-6">Create Account</h2>
              
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {errorMessage}
                </div>
              )}
              
              <input
                type="text"
                placeholder="Full Name"
                value={signupForm.name}
                onChange={(e) => setSignupForm({...signupForm, name: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ocean-500"
              />
              
              <input
                type="email"
                placeholder="Email"
                value={signupForm.email}
                onChange={(e) => setSignupForm({...signupForm, email: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ocean-500"
              />
              
              <input
                type="password"
                placeholder="Password"
                value={signupForm.password}
                onChange={(e) => setSignupForm({...signupForm, password: e.target.value})}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ocean-500"
              />
              
              <button
                onClick={handleSignup}
                className="w-full bg-ocean-600 text-white py-3 rounded-lg font-semibold hover:bg-ocean-700"
              >
                Sign Up
              </button>
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>
              
              {!window.Capacitor && (
              <button
                onClick={handleGoogleSignIn}
                className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>
              )}
              
              <p className="text-center text-gray-600 mt-4">
                Already have an account?{' '}
                <button onClick={() => setView('login')} className="text-ocean-600 font-semibold hover:underline">
                  Log in
                </button>
              </p>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="w-full bg-gradient-to-r from-ocean-500 to-peach-500 text-white py-3 rounded-lg font-semibold hover:from-ocean-600 hover:to-peach-600"
                >
                  View Premium Features
                </button>
                <p className="text-center text-xs text-gray-500 mt-2">
                  Subscribe anytime with your Apple ID
                </p>
              </div>
            </div>
          )}
      <div className="flex justify-center gap-4 mt-4 text-xs text-gray-500"><button onClick={() => setPublicPage("terms")} className="underline hover:text-gray-700">Terms of Use</button><span>•</span><button onClick={() => setPublicPage("privacy")} className="underline hover:text-gray-700">Privacy Policy</button></div>
        </div>
      </div>
      {showUpgradeModal && (

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-3xl font-bold text-ocean-900 mb-6">Premium Features</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="border-2 border-gray-200 rounded-xl p-6">
                <div className="text-center mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">Free</h3>
                  <p className="text-4xl font-bold text-gray-900 my-3">$0</p>
                  <p className="text-gray-600">Forever free</p>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2"><span className="text-green-600 mt-1">✓</span><span><strong>Up to 3 baby books</strong></span></li>
                  <li className="flex items-start gap-2"><span className="text-green-600 mt-1">✓</span><span><strong>2 people total</strong> (you + 1 contributor)</span></li>
                  <li className="flex items-start gap-2"><span className="text-green-600 mt-1">✓</span><span><strong>150 photos</strong> per book</span></li>
                  <li className="flex items-start gap-2"><span className="text-green-600 mt-1">✓</span><span><strong>Print physical books</strong> (pay per book)</span></li>
                </ul>
              </div>
              <div className="border-2 border-ocean-500 rounded-xl p-6 bg-gradient-to-br from-ocean-50 to-peach-50 relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-ocean-500 text-white px-4 py-1 rounded-full text-sm font-semibold">MOST POPULAR</div>
                <div className="text-center mb-4 mt-2">
                  <h3 className="text-2xl font-bold text-ocean-900">Premium</h3>
                  <p className="text-4xl font-bold text-ocean-600 my-3">$4.99</p>
                  <p className="text-ocean-700">per month</p>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2"><span className="text-green-600 mt-1">✓</span><span><strong>Up to 6 baby books</strong></span></li>
                  <li className="flex items-start gap-2"><span className="text-green-600 mt-1">✓</span><span><strong>6 people per book</strong></span></li>
                  <li className="flex items-start gap-2"><span className="text-green-600 mt-1">✓</span><span><strong>Unlimited photos</strong></span></li>
                  <li className="flex items-start gap-2"><span className="text-green-600 mt-1">✓</span><span><strong>Priority support</strong></span></li>
                </ul>
                <button onClick={async () => { const result = await purchasePremium(); if (result.success) { trackPremiumPurchased('upgrade_modal'); alert("Welcome to Premium! Your subscription is now active."); } setShowUpgradeModal(false); }} className="w-full bg-ocean-600 text-white py-3 rounded-lg hover:bg-ocean-700 font-semibold text-lg mt-6">Subscribe to Premium</button><p className="text-xs text-center text-ocean-700 mt-2">Your subscription is tied to your Apple ID and works across your devices</p>
              </div>
            </div>
            <div className="flex justify-center"><button onClick={() => setShowUpgradeModal(false)} className="text-gray-600 hover:text-gray-800 font-semibold">Maybe Later</button></div>
            <div className="flex justify-center mt-2"><button onClick={async () => { const result = await restorePurchases(); if (result.isPremium) { alert("Purchases restored!"); setShowUpgradeModal(false); } else { alert("No previous purchases found."); }}} className="text-ocean-600 hover:text-ocean-800 text-sm">Restore Purchases</button></div>
            <div className="flex justify-center gap-4 mt-4 text-xs text-gray-500"><button onClick={() => { setShowUpgradeModal(false); setPublicPage("terms"); }} className="underline hover:text-gray-700">Terms of Use</button><span>•</span><button onClick={() => { setShowUpgradeModal(false); setPublicPage("privacy"); }} className="underline hover:text-gray-700">Privacy Policy</button></div>
          </div>
        </div>
      )}
      </>
    );
  }

  return (
  <>
    <div className="min-h-screen bg-gradient-to-br from-ocean-50 to-sand-50">
      {showBabyBookSetup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setShowBabyBookSetup(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
              title="Close"
            >
              <X size={24} />
            </button>
            <h2 className="text-3xl font-bold text-ocean-900 mb-6 text-center">
              {babyBookSteps[babyBookStep].title}
            </h2>
            
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                {babyBookSteps.map((step, idx) => (
                  <div 
                    key={idx} 
                    className={`w-full h-2 ${idx <= babyBookStep ? 'bg-ocean-600' : 'bg-gray-200'} ${idx !== 0 ? 'ml-1' : ''} rounded`}
                  ></div>
                ))}
              </div>
              <p className="text-sm text-gray-600 text-center">Step {babyBookStep + 1} of {babyBookSteps.length}</p>
            </div>
            
            {babyBookSteps[babyBookStep].isCustom ? (
              <div className="space-y-4">
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <h3 className="font-semibold text-purple-900 mb-3">Add Family Members</h3>
                  <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <select
                      value={newFamilyMember.relation}
                      onChange={(e) => setNewFamilyMember({...newFamilyMember, relation: e.target.value})}
                      className="px-4 py-2 border rounded-lg w-full sm:w-auto sm:min-w-[180px]"
                    >
                      <option value="">Select Relation...</option>
                      <option value="Grandpa (Dad's Side)">Grandpa (Dad's Side)</option>
                      <option value="Grandma (Dad's Side)">Grandma (Dad's Side)</option>
                      <option value="Grandpa (Mom's Side)">Grandpa (Mom's Side)</option>
                      <option value="Grandma (Mom's Side)">Grandma (Mom's Side)</option>
                      <option value="Uncle">Uncle</option>
                      <option value="Aunt">Aunt</option>
                      <option value="Cousin">Cousin</option>
                      <option value="Godparent">Godparent</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Name"
                      value={newFamilyMember.name}
                      onChange={(e) => setNewFamilyMember({...newFamilyMember, name: e.target.value})}
                      className="flex-1 min-w-0 px-4 py-2 border rounded-lg"
                    />
                    <button
                      onClick={async () => {
                        if (newFamilyMember.relation && newFamilyMember.name) {
                          setBabyBookForm(prev => ({
                            ...prev,
                            familyTree: {
                              ...prev.familyTree,
                              [newFamilyMember.relation]: newFamilyMember.name
                            }
                          }));
                          setNewFamilyMember({ relation: '', name: '' });
                        }
                      }}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 w-full sm:w-auto"
                    >
                      Add
                    </button>
                  </div>
                  
                  {babyBookForm.familyTree && Object.entries(babyBookForm.familyTree).length > 0 && ( 
                    <div className="space-y-2">
                      {Object.entries(babyBookForm.familyTree).map(([relation, name]) => (
                        <div key={relation} className="flex justify-between items-center bg-white p-2 rounded gap-2">
                          <span className="truncate"><strong className="text-sm">{relation}:</strong> {name}</span>
                          <button
                            onClick={async () => {
                              const newTree = {...babyBookForm.familyTree};
                              delete newTree[relation];
                              setBabyBookForm(prev => ({...prev, familyTree: newTree}));
                            }}
                            className="text-red-600 hover:text-red-800 flex-shrink-0"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {babyBookSteps[babyBookStep].fields.map(field => (
                  <div key={field.key}>
                    <label className="block text-sm font-semibold mb-2">{field.label}</label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={babyBookForm[field.key]}
                        onChange={(e) => setBabyBookForm({...babyBookForm, [field.key]: e.target.value})}
                        rows={4}
                        className="w-full px-4 py-2 border rounded-lg"
                      />
                    ) : (
                      <input
                        type={field.type}
                        value={babyBookForm[field.key]}
                        onChange={(e) => setBabyBookForm({...babyBookForm, [field.key]: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex gap-3 mt-6">
              {babyBookStep > 0 && (
                <button
                  onClick={() => setBabyBookStep(babyBookStep - 1)}
                  className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                >
                  Back
                </button>
              )}
              
              {babyBookStep < babyBookSteps.length - 1 ? (
                <button
                  onClick={async () => {
                    // Step 0 requires baby name
                    if (babyBookStep === 0 && !babyBookForm.babyName) {
                      alert('Please enter a baby name before continuing');
                      return;
                    }
                    // Auto-save current step before advancing
                    if (babyBookId) {
                      await updateBabyBook(babyBookId, babyBookForm);
                    }
                    setBabyBookStep(babyBookStep + 1);
                  }}
                  className="flex-1 bg-ocean-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ocean-700"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={completeBabyBookSetup}
                  className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700"
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showMilestoneModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">
              {milestoneModal.type === 'Custom' ? 'Add Custom Milestone' : `Add Milestone: ${milestoneModal.type}`}
            </h3>
            <div className="space-y-4">
              {milestoneModal.type === 'Custom' && (
                <div>
                  <label className="block text-sm font-semibold mb-2">Milestone Name</label>
                  <input 
                    type="text" 
                    value={milestoneModal.customType || ''} 
                    onChange={(e) => setMilestoneModal({...milestoneModal, customType: e.target.value})} 
                    placeholder="e.g., First Tooth, First Day of School"
                    className="w-full px-4 py-2 border rounded-lg" 
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold mb-2">Date</label>
                <input 
                  type="date" 
                  value={milestoneModal.date} 
                  onChange={(e) => setMilestoneModal({...milestoneModal, date: e.target.value})} 
                  className="w-full px-4 py-2 border rounded-lg" 
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Description</label>
                <textarea 
                  value={milestoneModal.description} 
                  onChange={(e) => setMilestoneModal({...milestoneModal, description: e.target.value})} 
                  placeholder="Description..." 
                  rows={4} 
                  className="w-full px-4 py-2 border rounded-lg" 
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Photos (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleMilestonePhotoUpload}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                {milestoneModal.photos && milestoneModal.photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {milestoneModal.photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <img src={photo} alt="" className="w-full h-20 object-cover rounded" />
                        <button
                          onClick={() => removeMilestonePhoto(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button 
                onClick={async () => { 
                  setShowMilestoneModal(false); 
                  setMilestoneModal({ type: '', customType: '', description: '', date: new Date().toISOString().split('T')[0], photos: [] }); 
                }} 
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={addMilestoneFromModal} 
                className="flex-1 bg-ocean-600 text-white px-4 py-2 rounded-lg hover:bg-ocean-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Entry Modal */}
      {showEditEntryModal && editingEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={async () => {
                setShowEditEntryModal(false);
                setEditingEntry(null);
              }}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
              title="Close"
            >
              <X size={24} />
            </button>
            <h3 className="text-xl font-bold mb-4">Edit Entry</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Entry Text</label>
                <textarea
                  value={editingEntry.text || ''}
                  onChange={(e) => setEditingEntry({...editingEntry, text: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg h-32"
                  placeholder="What happened today?"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Milestone (optional)</label>
                <input
                  type="text"
                  value={editingEntry.milestone || ''}
                  onChange={(e) => setEditingEntry({...editingEntry, milestone: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="e.g., First smile, First word"
                />
              </div>

              {/* Existing Photos */}
              <div>
                <label className="block text-sm font-semibold mb-2">Photos</label>
                {editingEntry.photos && editingEntry.photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {editingEntry.photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <img src={photo} alt="" className="w-full h-24 object-cover rounded-lg" />
                        <button
                          onClick={async () => {
                            const newPhotos = editingEntry.photos.filter((_, i) => i !== idx);
                            setEditingEntry({...editingEntry, photos: newPhotos});
                          }}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add New Photos */}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    files.forEach(file => {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setEditingEntry(prev => ({
                          ...prev,
                          newPhotos: [...(prev.newPhotos || []), event.target.result]
                        }));
                      };
                      reader.readAsDataURL(file);
                    });
                  }}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                
                {/* Preview New Photos */}
                {editingEntry.newPhotos && editingEntry.newPhotos.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-2">New photos to add:</p>
                    <div className="grid grid-cols-3 gap-2">
                      {editingEntry.newPhotos.map((photo, idx) => (
                        <div key={idx} className="relative group">
                          <img src={photo} alt="" className="w-full h-24 object-cover rounded-lg border-2 border-green-400" />
                          <button
                            onClick={async () => {
                              const newPhotos = editingEntry.newPhotos.filter((_, i) => i !== idx);
                              setEditingEntry({...editingEntry, newPhotos: newPhotos});
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  onClick={async () => {
                    setShowEditEntryModal(false);
                    setEditingEntry(null);
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to delete this entry? This cannot be undone.')) {
                      const result = await deleteEntry(editingEntry.id, user.uid);
                      if (result.success) {
                        await selectBabyBook(babyBookId);
                        setShowEditEntryModal(false);
                        setEditingEntry(null);
                      } else {
                        alert('Error deleting entry: ' + result.error);
                      }
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
                <button
                  onClick={async () => {
                    const newPhotoCount = (editingEntry.newPhotos || []).length;
                    if (!userProfile?.isPremium && newPhotoCount > 0) {
                      const currentPhotoCount = currentBabyBook?.photoCount || 0;
                      if (currentPhotoCount + newPhotoCount > FREE_TIER_PHOTO_LIMIT) {
                        alert(`Free users can only have ${FREE_TIER_PHOTO_LIMIT} photos. You have ${currentPhotoCount} photos. Upgrade to Premium for unlimited photos!`);
                        setShowUpgradeModal(true);
                        return;
                      }
                    }
                    
                    const uploadedNewPhotos = [];
                    for (const photoBase64 of (editingEntry.newPhotos || [])) {
                      const result = await uploadPhotoFromBase64(user.uid, photoBase64);
                      if (result.success) {
                        uploadedNewPhotos.push(result.photoURL);
                      }
                    }
                    
                    const allPhotos = [...(editingEntry.photos || []), ...uploadedNewPhotos];
                    
                    const result = await updateEntry(editingEntry.id, {
                      text: editingEntry.text,
                      milestone: editingEntry.milestone,
                      milestoneType: editingEntry.milestoneType,
                      photos: allPhotos
                    });
                    
                    if (result.success) {
                      await selectBabyBook(babyBookId);
                      setShowEditEntryModal(false);
                      setEditingEntry(null);
                    } else {
                      alert('Error updating entry: ' + result.error);
                    }
                  }}
                  className="flex-1 bg-ocean-600 text-white px-4 py-2 rounded-lg hover:bg-ocean-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            {!bulkUploadProgress.processing ? (
              <div className="space-y-4">
                <h3 className="text-xl font-bold">Bulk Upload Photos</h3>
                
                <p className="text-gray-600">
                  Select multiple photos from your phone or computer. Each photo will be automatically added to your journal based on when it was taken.
                </p>
                
                <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                  <p className="text-sm text-purple-900 font-semibold mb-2">📸 How it works:</p>
                  <ul className="text-sm text-purple-800 space-y-1">
                    <li>• Photos are dated based on when they were taken</li>
                    <li>• Each photo becomes its own journal entry</li>
                    <li>• You can add captions later</li>
                    <li>• Photos automatically sort into correct time periods</li>
                  </ul>
                </div>

                <div className="border-2 border-purple-300 border-dashed rounded-lg p-6 text-center">
                  <input
                    id="bulk-upload-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleBulkPhotoUpload}
                    className="hidden"
                  />
                  <label 
                    htmlFor="bulk-upload-input"
                    className="cursor-pointer inline-block bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700"
                  >
                    Choose Photos
                  </label>
                  <p className="text-sm text-gray-500 mt-2">Click to select photos from your device</p>
                </div>

                <button 
                  onClick={() => setShowBulkUpload(false)} 
                  className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="mb-4">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600"></div>
                  </div>
                  <p className="text-lg font-semibold mb-2">Processing Photos...</p>
                  <p className="text-gray-600">
                    {bulkUploadProgress.current} of {bulkUploadProgress.total} photos uploaded
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
                    <div 
                      className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${bulkUploadProgress.total > 0 ? (bulkUploadProgress.current / bulkUploadProgress.total) * 100 : 0}%` }}
                    ></div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-4">Please wait, this may take a moment...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Invite User to Baby Book</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Email Address</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  placeholder="user@example.com"
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="viewer">Viewer (Read-only)</option>
                  <option value="editor">Editor (Can add entries)</option>
                  <option value="admin">Admin (Full access)</option>
                </select>
              </div>
              
              <div className="bg-blue-50 p-3 rounded text-sm">
                <p className="font-semibold mb-1">Role Permissions:</p>
                {inviteForm.role === 'viewer' && <p>• Can view all entries and photos</p>}
                {inviteForm.role === 'editor' && (
                  <>
                    <p>• Can add new entries and milestones</p>
                    <p>• Can edit their own entries</p>
                    <p>• Can view all content</p>
                  </>
                )}                {inviteForm.role === 'admin' && (
                  <>
                    <p>• Full access to all features</p>
                    <p>• Can edit any entry</p>
                    <p>• Can manage users</p>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={async () => {
                  setShowInviteModal(false);
                  setInviteForm({ email: '', role: 'viewer' });
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
              onClick={async () => {
                  if (!inviteForm.email) {
                    alert('Please enter an email address');
                    return;
                  }
                  
                  // Check contributor limit for free users
                  const memberCount = Object.keys(currentBabyBook?.members || {}).length;
                  if (!userProfile?.isPremium && memberCount >= FREE_TIER_CONTRIBUTOR_LIMIT) {
                    alert(`Free users can only have ${FREE_TIER_CONTRIBUTOR_LIMIT} people total (you + 1 contributor). Upgrade to Premium to invite more!`);
                    setShowUpgradeModal(true);
                    return;
                  }
                  
                  const result = await inviteUserToBabyBook(
                    babyBookId,
                    inviteForm.email,
                    inviteForm.role,
                    user.uid
                  );
                  
                  if (result.success) {
                    alert(`Invitation sent to ${inviteForm.email}! They will receive access as ${inviteForm.role}.`);
                    setShowInviteModal(false);
                    setInviteForm({ email: '', role: 'viewer' });
                  } else {
                    alert('Error sending invitation: ' + result.error);
                  }
                }}
                className="flex-1 bg-ocean-600 text-white px-4 py-2 rounded-lg hover:bg-ocean-700"
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Upgrade to Premium Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-3xl font-bold text-ocean-900 mb-6">Upgrade to Premium</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Free Plan */}
              <div className="border-2 border-gray-200 rounded-xl p-6">
                <div className="text-center mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">Free</h3>
                  <p className="text-4xl font-bold text-gray-900 my-3">$0</p>
                  <p className="text-gray-600">Forever free</p>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span><strong>Up to 3 baby books</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span><strong>2 people total</strong> (you + 1 contributor)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span><strong>150 photos</strong> per book</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">✓</span>
                    <span><strong>Print physical books</strong> (pay per book)</span>
                  </li>
                </ul>
              </div>
              
              {/* Premium Plan */}
              <div className="border-2 border-ocean-500 rounded-xl p-6 bg-gradient-to-br from-ocean-50 to-peach-50 relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-ocean-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  MOST POPULAR
                </div>
                
                <div className="text-center mb-4 mt-2">
                  <h3 className="text-2xl font-bold text-ocean-900">Premium</h3>
                  <p className="text-4xl font-bold text-ocean-600 my-3">$4.99</p>
                  <p className="text-ocean-700">per month</p>
                  <p className="text-sm text-ocean-600 mt-1">+ $1.99/month per additional book after 6</p>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1 text-xl">✓</span>
                    <span><strong>Up to 6 baby books</strong> included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1 text-xl">✓</span>
                    <span><strong>Additional books after 6</strong> at $1.99/mo each</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1 text-xl">✓</span>
                    <span><strong>Up to 6 people per book</strong> (you + 5 contributors)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1 text-xl">✓</span>
                    <span><strong>Unlimited photos</strong> & storage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1 text-xl">✓</span>
                    <span><strong>Up to 6 baby books</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-1 text-xl">✓</span>
                    <span><strong>Priority support</strong></span>
                  </li>
                </ul>
                
                <button
                  onClick={async () => {
                    const result = await purchasePremium(); if (result.success) { trackPremiumPurchased('settings'); await updateUserProfile(user.uid, { isPremium: true }); setUserProfile(prev => ({ ...prev, isPremium: true })); alert("Welcome to Premium!"); }
                    setShowUpgradeModal(false);
                  }}
                  className="w-full bg-ocean-600 text-white py-3 rounded-lg hover:bg-ocean-700 font-semibold text-lg mt-6"
                >
                  Upgrade to Premium
                </button>
              </div>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-600 hover:text-gray-800 font-semibold"
              >
                Maybe Later
              </button>
            </div>
            <div className="flex justify-center mt-2">
              <button
                onClick={async () => {
                  const result = await restorePurchases();
                  if (result.isPremium) {
                    await updateUserProfile(user.uid, { isPremium: true });
                    setUserProfile(prev => ({ ...prev, isPremium: true }));
                    alert("Purchases restored! Welcome back to Premium.");
                    setShowUpgradeModal(false);
                  } else {
                    alert("No previous purchases found.");
                  }
                }}
                className="text-ocean-600 hover:text-ocean-800 text-sm"
              >
                Restore Purchases
              </button>
            </div>
            <div className="flex justify-center gap-4 mt-4 text-xs text-gray-500">
              <button onClick={() => { setShowUpgradeModal(false); setView('terms'); }} className="underline hover:text-gray-700">Terms of Use</button>
              <span>•</span>
              <button onClick={() => { setShowUpgradeModal(false); setView('privacy'); }} className="underline hover:text-gray-700">Privacy Policy</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Baby Book Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            {deleteStep === 1 && (
              <>
                <h3 className="text-xl font-bold text-red-700 mb-4">Delete Baby Book</h3>
                <p className="text-gray-700 mb-4">
                  Are you sure you want to delete "<strong>{currentBabyBook?.babyName}</strong>"?
                </p>
                <p className="text-sm text-gray-600 mb-6">
                  This will permanently delete all entries, photos, and milestones. This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      setShowDeleteModal(false);
                      setDeleteStep(1);
                      setDeletePassword('');
                      setDeleteError('');
                    }}
                    className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setDeleteStep(2)}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                  >
                    Continue
                  </button>
                </div>
              </>
            )}

            {deleteStep === 2 && (
              <>
                <h3 className="text-xl font-bold text-red-700 mb-4">Verify Your Password</h3>
                <p className="text-gray-700 mb-4">
                  Please enter your password to confirm deletion of "<strong>{currentBabyBook?.babyName}</strong>".
                </p>
                {deleteError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    {deleteError}
                  </div>
                )}
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border rounded-lg mb-4 focus:ring-2 focus:ring-red-500"
                />
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      setDeleteStep(1);
                      setDeletePassword('');
                      setDeleteError('');
                    }}
                    className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={async () => {
                      if (!deletePassword) {
                        setDeleteError('Please enter your password');
                        return;
                      }
                      try {
                        const credential = EmailAuthProvider.credential(user.email, deletePassword);
                        await reauthenticateWithCredential(auth, credential);
                        setDeleteError('');
                        setDeleteStep(3);
                      } catch (error) {
                        setDeleteError('Incorrect password. Please try again.');
                      }
                    }}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                  >
                    Verify
                  </button>
                </div>
              </>
            )}

            {deleteStep === 3 && (
              <>
                <h3 className="text-xl font-bold text-red-700 mb-4">Final Confirmation</h3>
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
                  <p className="text-red-800 font-semibold">⚠️ FINAL WARNING</p>
                  <p className="text-red-700 text-sm mt-2">
                    You are about to permanently delete "<strong>{currentBabyBook?.babyName}</strong>" and all its contents. This action CANNOT be undone.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      setShowDeleteModal(false);
                      setDeleteStep(1);
                      setDeletePassword('');
                      setDeleteError('');
                    }}
                    className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      const bookIdToDelete = babyBookId;
                      const result = await deleteBabyBook(bookIdToDelete);
                      if (result.success) {
                        setShowDeleteModal(false);
                        setDeleteStep(1);
                        setDeletePassword('');
                        setDeleteError('');
                        alert('Baby book deleted successfully');
                        const reloadResult = await getUserBabyBooks(user.uid);
                        if (reloadResult.success && reloadResult.babyBooks.length > 0) {
                          setBabyBooks(reloadResult.babyBooks);
                          await selectBabyBook(reloadResult.babyBooks[0].id);
                        } else {
                          setBabyBooks([]);
                          setCurrentBabyBook(null);
                          setBabyBookId(null);
                        }
                        setView('home');
                      } else {
                        setDeleteError('Error deleting baby book: ' + result.error);
                      }
                    }}
                    className="flex-1 bg-red-700 text-white px-4 py-2 rounded-lg hover:bg-red-800 font-semibold"
                  >
                    Delete Forever
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <nav className="bg-white shadow-sm border-b" style={{paddingTop: '50px'}}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
          {/* Top row on mobile: Logo and Nav Icons */}
          <div className="flex justify-between items-center w-full md:w-auto">
            <h1 className="text-2xl font-bold text-ocean-900 cursor-pointer hover:text-ocean-700" onClick={() => setView('home')}>BabyBookin</h1>
            
            <div className="flex gap-2">
              <button onClick={() => setView('home')} className={`p-2 rounded-lg ${view === 'home' ? 'bg-ocean-100' : 'hover:bg-gray-100'}`} title="Home">
                <Home size={20} />
              </button>
              <button onClick={() => setView('journal')} className={`p-2 rounded-lg ${view === 'journal' ? 'bg-ocean-100' : 'hover:bg-gray-100'}`} title="Journal">
                <Book size={20} />
              </button>
              <button onClick={() => setView('milestones')} className={`p-2 rounded-lg ${view === 'milestones' ? 'bg-ocean-100' : 'hover:bg-gray-100'}`} title="Milestones">
                <Star size={20} />
              </button>
              <button 
                onClick={async () => { 
                  if (currentBabyBook && babyBookId) {
                    setBabyBookForm(currentBabyBook);
                    setShowBabyBookSetup(true); 
                    setBabyBookStep(0);
                  } else {
                    setShowBabyBookSetup(true); 
                    setBabyBookStep(0);
                  }
                }} 
                className="p-2 rounded-lg hover:bg-gray-100" 
                title="Baby Profile"
              >
                <Baby size={20} />
              </button>
              <button onClick={() => setView('settings')} className={`p-2 rounded-lg ${view === 'settings' ? 'bg-ocean-100' : 'hover:bg-gray-100'}`} title="Settings">
                <Settings size={20} />
              </button>
            </div>
          </div>

          {/* Second row on mobile: Baby Book Selector */}
          {babyBooks.length > 0 && currentBabyBook && (
            <div className="relative w-full md:w-auto">
              <button
                onClick={() => setShowBabyBookSelector(!showBabyBookSelector)}
                className="flex items-center gap-2 px-3 py-2 bg-ocean-50 hover:bg-ocean-100 rounded-lg border border-ocean-200 w-full md:w-auto justify-between md:justify-start"
              >
                <Baby size={16} className="text-ocean-600" />
                <span className="font-semibold text-ocean-900">{currentBabyBook.babyName}</span>
                <ChevronDown size={16} className="text-ocean-600" />
              </button>
              
              {showBabyBookSelector && (
                <div className="absolute top-full mt-2 left-0 bg-white border rounded-lg shadow-lg min-w-[250px] z-50">
                  <div className="p-2">
                    <div className="text-xs text-gray-500 px-2 py-1 font-semibold">YOUR BOOKS</div>
                    {babyBooks.filter(book => book.ownerId === user?.uid).map(book => (
                      <button
                        key={book.id}
                        onClick={async () => {
                          selectBabyBook(book.id);
                          setShowBabyBookSelector(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded hover:bg-ocean-50 flex items-center justify-between ${
                          book.id === babyBookId ? 'bg-ocean-100' : ''
                        }`}
                      >
                        <span className="font-medium">{book.babyName}</span>
                        {book.id === babyBookId && <span className="text-ocean-600">✓</span>}
                      </button>
                    ))}
                    
                    {babyBooks.filter(book => book.ownerId !== user?.uid).length > 0 && (
                      <>
                        <div className="text-xs text-gray-500 px-2 py-1 font-semibold mt-2">SHARED WITH YOU</div>
                        {babyBooks.filter(book => book.ownerId !== user?.uid).map(book => (
                          <button
                            key={book.id}
                            onClick={async () => {
                              selectBabyBook(book.id);
                              setShowBabyBookSelector(false);
                            }}
                            className={`w-full text-left px-3 py-2 rounded hover:bg-ocean-50 flex items-center justify-between ${
                              book.id === babyBookId ? 'bg-ocean-100' : ''
                            }`}
                          >
                            <span>{book.babyName}</span>
                            {book.id === babyBookId && <span className="text-ocean-600">✓</span>}
                          </button>
                        ))}
                      </>
                    )}
                    
                    {/* Add New Baby Book Button */}
                    <div className="border-t mt-2 pt-2">
                      <button
                        onClick={async () => {
                          // Check if user can create more books
                          const ownedBooks = babyBooks.filter(book => book.ownerId === user?.uid);
                          if (!userProfile?.isPremium && ownedBooks.length >= FREE_TIER_BOOK_LIMIT) {
                            alert(`Free users can have up to ${FREE_TIER_BOOK_LIMIT} baby books. Upgrade to Premium for more!`);
                            setShowUpgradeModal(true);
                            setShowBabyBookSelector(false);
                            return;
                          }
                          
                          // Allow creating new book
                          setBabyBookId(null);
                          setBabyBookForm({
                            completed: false,
                            babyName: '',
                            momName: '',
                            dadName: '',
                            birthDate: '',
                            gender: '',
                            familyTree: {}
                          });
                          setShowBabyBookSetup(true);
                          setBabyBookStep(0);
                          setShowBabyBookSelector(false);
                        }}
                        className="w-full text-left px-3 py-2 rounded hover:bg-ocean-50 flex items-center gap-2 text-ocean-600 font-semibold"
                      >
                        <Plus size={16} />
                        <span>Create New Baby Book</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-6xl mx-auto p-8">
        {/* Pending Invitations Banner */}
        {pendingInvitations.length > 0 && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-blue-900">You have pending invitations!</h3>
                <p className="text-blue-700">You've been invited to join {pendingInvitations.length} baby book{pendingInvitations.length > 1 ? 's' : ''}.</p>
              </div>
              <button
                onClick={async () => {
                  const invitation = pendingInvitations[0];
                  const result = await acceptInvitation(invitation.id, user.uid);
                  if (result.success) {
                    alert(`You've been added to ${invitation.babyBookName || 'the baby book'}!`);
                    await loadUserBabyBooks(user.uid, user.email);
                    setPendingInvitations(pendingInvitations.filter(inv => inv.id !== invitation.id));
                  } else {
                    alert('Error accepting invitation: ' + result.error);
                  }
                }}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
              >
                Accept Invitation
              </button>
            </div>
          </div>
        )}
        
        {view === 'home' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <button onClick={() => setView('add')} className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg border-2 border-ocean-200">
                <Plus size={48} className="mx-auto mb-4 text-ocean-600" />
                <h2 className="text-2xl font-bold">Add Entry</h2>
              </button>
              <button onClick={() => setView('journal')} className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg border-2 border-ocean-200">
                <Book size={48} className="mx-auto mb-4 text-ocean-600" />
                <h2 className="text-2xl font-bold">View Journal</h2>
              </button>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-sand-50 p-6 rounded-xl shadow-md border-2 border-purple-200">
              <button onClick={() => setShowBulkUpload(true)} className="w-full">
                <div className="flex items-center justify-center gap-3">
                  <Calendar size={32} className="text-purple-600" />
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-purple-900">Bulk Upload Photos</h3>
                    <p className="text-sm text-purple-700">Upload multiple photos at once - they'll auto-date based on when the photo was taken</p>
                  </div>
                </div>
              </button>
            </div>
            
            
            {showShareTip && (
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl shadow-md border-2 border-blue-200 relative">
                <button 
                  onClick={async () => {
                    setShowShareTip(false);
                    localStorage.setItem('babybookin_share_tip_dismissed', 'true');
                  }}
                  className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                >
                  <X size={20} />
                </button>
                <div className="flex items-start gap-3 pr-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Share size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-blue-900">📸 Tip: Share photos directly to BabyBookin!</h3>
                    <p className="text-sm text-blue-700 mt-1">Take photos with your camera, then tap Share → BabyBookin to instantly create a new entry with your photos.</p>
                  </div>
                </div>
              </div>
            )}
            {entries.length > 0 && (
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-bold mb-4">Recent Entries</h3>
                {[...entries].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 3).map(e => (
                  <div key={e.id} className="border-l-4 border-ocean-400 pl-4 py-2 mb-3">
                    <p className="text-sm text-gray-500">{new Date(e.timestamp).toLocaleDateString()}</p>
                    {e.milestone && <p className="font-semibold text-ocean-700">{e.milestoneType}: {e.milestone}</p>}
                    {e.text && <p className="text-gray-700">{e.text}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === 'add' && (
          <div className="bg-white p-8 rounded-xl shadow-md max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">New Entry</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Entry Date (optional - leave blank for today)</label>
                <div className="grid grid-cols-2 gap-3">
                  <input 
                    type="date" 
                    value={newEntry.customDate || ''} 
                    onChange={(e) => setNewEntry(prev => ({...prev, customDate: e.target.value}))} 
                    className="w-full px-4 py-2 border rounded-lg" 
                  />
                  <input 
                    type="time" 
                    value={newEntry.customTime || ''} 
                    onChange={(e) => setNewEntry(prev => ({...prev, customTime: e.target.value}))} 
                    className="w-full px-4 py-2 border rounded-lg" 
                  />
                </div>
              </div>
              
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold mb-2">
                  <Star size={18} className="text-ocean-500" />
                  Milestone (optional)
                </label>
                <select
                  value={newEntry.milestoneType}
                  onChange={(e) => setNewEntry({...newEntry, milestoneType: e.target.value, customMilestoneType: ''})}
                  className="w-full px-4 py-2 border rounded-lg mb-2"
                >
                  <option value="">Select milestone type...</option>
                  {milestoneTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                  <option value="Custom">Custom...</option>
                </select>
                {newEntry.milestoneType === 'Custom' && (
                  <input
                    type="text"
                    value={newEntry.customMilestoneType}
                    onChange={(e) => setNewEntry({...newEntry, customMilestoneType: e.target.value})}
                    placeholder="Custom milestone name (e.g., First Haircut)"
                    className="w-full px-4 py-2 border rounded-lg mb-2"
                  />
                )}
                {newEntry.milestoneType && (
                  <input 
                    type="text" 
                    value={newEntry.milestone} 
                    onChange={(e) => setNewEntry({...newEntry, milestone: e.target.value})} 
                    placeholder="Describe milestone..." 
                    className="w-full px-4 py-2 border rounded-lg" 
                  />
                )}
              </div>
              
              <textarea 
                value={newEntry.text} 
                onChange={(e) => setNewEntry({...newEntry, text: e.target.value})} 
                placeholder="Your thoughts..." 
                rows={8} 
                className="w-full px-4 py-2 border rounded-lg" 
              />
              
              <div>
                <label className="block text-sm font-semibold mb-2">Photos (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                {newEntry.photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    {newEntry.photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <img src={photo} alt="" className="w-full h-24 object-cover rounded-lg" />
                        <button
                          onClick={() => removePhoto(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={addEntry} 
                  className="flex-1 bg-ocean-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ocean-700"
                >
                  Save
                </button>
                <button 
                  onClick={async () => { 
                    setNewEntry({ text: '', milestone: '', milestoneType: '', customMilestoneType: '', customDate: '', customTime: '', photos: [] }); 
                    setView('home'); 
                  }} 
                  className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {view === 'journal' && (
          <div className="space-y-6">
            {currentBabyBook?.babyName && (
              <div className="bg-sand-50 p-4 sm:p-6 rounded-xl shadow-md border-l-4 border-pink-400">
                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Baby Photo */}
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-pink-100 to-ocean-100 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                      {currentBabyBook.babyPhoto ? (
                        <img 
                          src={currentBabyBook.babyPhoto} 
                          alt={currentBabyBook.babyName} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-center">
                          <Baby size={32} className="mx-auto text-pink-300" />
                        </div>
                      )}
                    </div>
                    <label className="block mt-2 text-xs text-ocean-600 hover:text-ocean-800 w-full text-center cursor-pointer font-semibold">
                      {currentBabyBook.babyPhoto ? 'Change' : 'Add Photo'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          const reader = new FileReader();
                          reader.onload = async (event) => {
                            const base64 = event.target.result;
                            const result = await uploadPhotoFromBase64(user.uid, base64);
                            if (result.success) {
                              await updateBabyBook(babyBookId, { babyPhoto: result.photoURL });
                              setCurrentBabyBook(prev => ({ ...prev, babyPhoto: result.photoURL }));
                            } else {
                              alert('Error uploading photo: ' + result.error);
                            }
                          };
                          reader.readAsDataURL(file);
                        }}
                      />
                    </label>
                  </div>
                  {/* Baby Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-pink-900 mb-2">Baby's Info</h3>
                    <p className="text-sm sm:text-base truncate"><strong>Name:</strong> {currentBabyBook.babyName}</p>
                    {currentBabyBook.birthDate && <p className="text-sm sm:text-base"><strong>Born:</strong> {new Date(currentBabyBook.birthDate).toLocaleDateString()}</p>}
                    {currentBabyBook.birthTime && <p className="text-sm sm:text-base"><strong>Time:</strong> {currentBabyBook.birthTime}</p>}
                  </div>
                </div>
              </div>
            )}
            
            {currentBabyBook?.momName && (
              <div className="bg-sand-50 p-6 rounded-xl shadow-md border-l-4 border-pink-400">
                <h3 className="text-xl font-bold text-pink-900 mb-4">Parents Story</h3>
                <p><strong>Mother:</strong> {currentBabyBook.momName}</p>
                <p><strong>Father:</strong> {currentBabyBook.dadName}</p>
                {currentBabyBook.howTheyMet && <p className="mt-3"><strong>How They Met:</strong> {currentBabyBook.howTheyMet}</p>}
                {currentBabyBook.aboutMom && <p className="mt-3"><strong>About Mom:</strong> {currentBabyBook.aboutMom}</p>}
                {currentBabyBook.aboutDad && <p className="mt-3"><strong>About Dad:</strong> {currentBabyBook.aboutDad}</p>}
              </div>
            )}
            
            {(currentBabyBook?.howFoundOut || currentBabyBook?.siblingReactions || currentBabyBook?.howSharedNews) && (
              <div className="bg-sand-50 p-6 rounded-xl shadow-md border-l-4 border-pink-400">
                <h3 className="text-xl font-bold text-pink-900 mb-4">The Pregnancy News</h3>
                {currentBabyBook.howFoundOut && <p className="mb-3"><strong>How We Found Out:</strong> {currentBabyBook.howFoundOut}</p>}
                {currentBabyBook.siblingReactions && <p className="mb-3"><strong>Sibling Reactions:</strong> {currentBabyBook.siblingReactions}</p>}
                {currentBabyBook.howSharedNews && <p><strong>Sharing the News:</strong> {currentBabyBook.howSharedNews}</p>}
              </div>
            )}
            
            {(currentBabyBook?.pregnancyNotes || currentBabyBook?.cravings) && (
              <div className="bg-sand-50 p-6 rounded-xl shadow-md border-l-4 border-pink-400">
                <h3 className="text-xl font-bold text-pink-900 mb-4">Pregnancy Journey</h3>
                {currentBabyBook.pregnancyNotes && <p className="mb-3"><strong>Pregnancy Notes:</strong> {currentBabyBook.pregnancyNotes}</p>}
                {currentBabyBook.cravings && <p><strong>Cravings:</strong> {currentBabyBook.cravings}</p>}
              </div>
            )}
            
            {(currentBabyBook?.nameStory || currentBabyBook?.babyShower) && (
              <div className="bg-sand-50 p-6 rounded-xl shadow-md border-l-4 border-pink-400">
                <h3 className="text-xl font-bold text-pink-900 mb-4">Preparing for Baby</h3>
                {currentBabyBook.nameStory && <p className="mb-3"><strong>How We Picked Your Name:</strong> {currentBabyBook.nameStory}</p>}
                {currentBabyBook.babyShower && <p><strong>Baby Shower:</strong> {currentBabyBook.babyShower}</p>}
              </div>
            )}
            
            {currentBabyBook?.hospital && (
              <div className="bg-sand-50 p-6 rounded-xl shadow-md border-l-4 border-pink-400">
                <h3 className="text-xl font-bold text-pink-900 mb-4">Birth Details</h3>
                {currentBabyBook.hospital && <p><strong>Hospital:</strong> {currentBabyBook.hospital}</p>}
                {currentBabyBook.doctorName && <p><strong>Doctor:</strong> {currentBabyBook.doctorName}</p>}
                {currentBabyBook.weight && <p><strong>Weight:</strong> {currentBabyBook.weight}</p>}
                {currentBabyBook.length && <p><strong>Length:</strong> {currentBabyBook.length}</p>}
                {currentBabyBook.siblings && <p className="mt-3"><strong>Siblings:</strong> {currentBabyBook.siblings}</p>}
              </div>
            )}
            
            {currentBabyBook?.familyTree && Object.keys(currentBabyBook.familyTree).filter(k => currentBabyBook.familyTree[k]).length > 0 && (
              <div className="bg-sand-50 p-6 rounded-xl shadow-md border-l-4 border-pink-400">
                <h3 className="text-xl font-bold text-pink-900 mb-4">Family Tree</h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(currentBabyBook.familyTree).filter(([_, name]) => name).map(([relation, name]) => (
                    <div key={relation} className="bg-white p-3 rounded">
                      <p className="text-sm text-gray-600">{relation}</p>
                      <p className="font-semibold">{name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {sortPeriods(Object.keys(getEntriesByPeriod())).map(period => (
              <div key={period} className="mb-8">
                <h2 className="text-2xl font-bold text-ocean-900 mb-4 border-b-2 border-ocean-400 pb-2">{period}</h2>
                <div className="space-y-4">
                  {getEntriesByPeriod()[period].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).map(e => (
                    <div key={e.id} className="bg-white p-6 rounded-xl shadow-md border-l-4 border-ocean-400">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-sm text-gray-500">{new Date(e.timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        {(e.authorId === user?.uid || currentBabyBook?.members?.[user?.uid] === 'admin') && (
                          <button
                            onClick={async () => {
                              setEditingEntry(e);
                              setShowEditEntryModal(true);
                            }}
                            className="text-ocean-600 hover:text-ocean-800 text-sm font-semibold flex items-center gap-1"
                          >
                            <span>Edit</span>
                          </button>
                        )}
                      </div>
                        {e.milestone && (
                        <div className="bg-ocean-50 px-3 py-2 rounded-lg mb-3 inline-block">
                          <Star size={18} className="inline text-ocean-500 mr-2" />
                          <span className="font-semibold text-ocean-900">{e.milestoneType}: {e.milestone}</span>
                        </div>
                      )}
                      {e.text && <p className="text-gray-800 whitespace-pre-wrap mb-4">{e.text}</p>}
                      {e.photos && e.photos.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {e.photos.map((photo, idx) => (
                            <img key={idx} src={photo} alt="" className="w-full h-40 object-cover rounded-lg" />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            

            {/* Print Book Button */}
            {entries.length > 0 && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setShowPrintBook(true)}
                  className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-ocean-500 to-peach-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                >
                  <Book size={24} />
                  <div className="text-left">
                    <p className="text-lg">Print Your Book</p>
                    <p className="text-xs opacity-90">Create a beautiful printed memory book</p>
                  </div>
                </button>
              </div>
            )}
            {entries.length === 0 && !currentBabyBook?.birthDate && <p className="text-center text-gray-500 py-12">No entries yet</p>}
          </div>
        )}

        {view === 'milestones' && (
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="flex flex-col gap-4 mb-6">
              <h2 className="text-2xl font-bold">Milestone Tracker</h2>
              <button 
                onClick={async () => { 
                  setMilestoneModal({ type: 'Custom', customType: '', description: '', date: new Date().toISOString().split('T')[0], photos: [] }); 
                  setShowMilestoneModal(true); 
                }} 
                className="bg-ocean-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-ocean-700 w-full md:w-auto md:self-start"
              >
                <Plus size={20} />
                Add Custom Milestone
              </button>
            </div>
            
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-700">Standard Milestones</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {milestoneTypes.map(type => {
                  const achieved = milestones.find(m => m.type === type);
                  return (
                    <div key={type} className={`p-4 rounded-lg border-2 ${achieved ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold">{type}</h3>
                        {achieved && <Check size={20} className="text-green-600" />}
                      </div>
                      {achieved ? (
                        <div>
                          <p className="text-sm text-gray-600">{new Date(achieved.date).toLocaleDateString()}</p>
                          <p className="text-sm text-gray-700 mt-1">{achieved.description}</p>
                          {achieved.photos && achieved.photos.length > 0 && (
                            <div className="mt-2">
                              <img src={achieved.photos[0]} alt="" className="w-full h-24 object-cover rounded" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <button 
                          onClick={async () => { 
                            setMilestoneModal({ type, description: '', date: new Date().toISOString().split('T')[0], photos: [] }); 
                            setShowMilestoneModal(true); 
                          }} 
                          className="text-sm text-ocean-600 font-semibold hover:underline"
                        >
                          Mark achieved
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {(() => {
              const customMilestones = milestones.filter(m => !milestoneTypes.includes(m.type));
              if (customMilestones.length > 0) {
                return (
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-gray-700">Custom Milestones</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      {customMilestones.map(m => (
                        <div key={m.id} className="p-4 rounded-lg border-2 bg-purple-50 border-purple-300">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold">{m.type}</h3>
                            <Check size={20} className="text-purple-600" />
                          </div>
                          <p className="text-sm text-gray-600">{new Date(m.date).toLocaleDateString()}</p>
                          <p className="text-sm text-gray-700 mt-1">{m.description}</p>
                          {m.photos && m.photos.length > 0 && (
                            <div className="mt-2">
                              <img src={m.photos[0]} alt="" className="w-full h-24 object-cover rounded" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
        {view === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h2 className="text-2xl font-bold mb-6">Account Settings</h2>
              {/* Photo Usage & Premium Status */}
              
              <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-ocean-50 to-sand-50 border border-ocean-200">
                {!userProfile?.isPremium ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-ocean-900">Free Plan</h3>
                      <span className="text-2xl">📸</span>
                    </div>
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-700">Photo Storage</span>
                        <span className="font-semibold text-gray-900">{currentBabyBook?.photoCount || 0} / {FREE_TIER_PHOTO_LIMIT} photos</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${(currentBabyBook?.photoCount || 0) >= 45 ? 'bg-red-500' : (currentBabyBook?.photoCount || 0) >= 30 ? 'bg-ocean-500' : 'bg-green-500'}`}
                          style={{width: `${Math.min(((currentBabyBook?.photoCount || 0) / FREE_TIER_PHOTO_LIMIT) * 100, 100)}%`}}
                        ></div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="w-full bg-ocean-600 text-white py-2 rounded-lg hover:bg-ocean-700 font-semibold"
                    >
                      Upgrade to Premium - $4.99/month
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-3xl mb-2">✨</div>
                    <h3 className="text-xl font-bold text-ocean-900 mb-1">Premium Member</h3>
                    <p className="text-gray-700">Unlimited photos • Unlimited family members</p>
                  </div>
                )}
              </div>
              
              <div className="space-y-6">
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4">Profile</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Name</label>
                      <p className="text-gray-700">{user?.displayName || user?.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Email</label>
                      <p className="text-gray-700">{user?.email}</p>
                    </div>
                  </div>
                </div>
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4">Daily Reminders</h3>
                  <p className="text-sm text-gray-600 mb-4">Get a daily push notification to capture memories for {currentBabyBook?.babyName || 'your baby'}.</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold">Daily reminder at 9:00 AM</label>
                      <button
                        onClick={async () => {
                          const newValue = !userProfile?.dailyReminders;
                          const result = await updateUserProfile(user.uid, { dailyReminders: newValue });
                          if (result.success) {
                            setUserProfile(prev => ({ ...prev, dailyReminders: newValue }));
                          } else {
                            alert('Error saving preferences');
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userProfile?.dailyReminders ? 'bg-ocean-600' : 'bg-gray-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${userProfile?.dailyReminders ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    {userProfile?.dailyReminders && (
                      <p className="text-sm text-green-600">
                        ✓ You'll receive a daily reminder to add memories
                      </p>
                    )}
                    {!userProfile?.fcmToken && userProfile?.dailyReminders && (
                      <p className="text-sm text-ocean-600">
                        Push notifications need to be enabled on your device for reminders to work.
                      </p>
                    )}
                  </div>
                </div>
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4">Baby Book Members</h3>
                  {currentBabyBook?.members && (
                    <div className="space-y-3 mb-4">
                      {Object.entries(currentBabyBook.members).map(([userId, role]) => (
                        <div key={userId} className="flex flex-col sm:flex-row sm:justify-between sm:items-center bg-gray-50 p-3 rounded gap-2">
                          <div>
                            <p className="font-semibold">{userId === user.uid ? 'You' : 'Member'}</p>
                            <p className="text-sm text-gray-600 capitalize">{role}</p>
                          </div>
                          {userId !== user.uid && role !== 'admin' && (
                            <button onClick={() => removeMember(userId)} className="text-red-600 hover:text-red-800 text-sm self-start sm:self-auto">
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="bg-ocean-600 text-white px-4 py-2 rounded-lg hover:bg-ocean-700 flex items-center gap-2"
                  > 
                    <Plus size={20} />
                    Invite User
                  </button>
                </div>
                
                {/* Leave Book button for shared books */}
                {currentBabyBook && currentBabyBook.ownerId !== user?.uid && (
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Leave Baby Book</h3>
                    <p className="text-sm text-gray-600 mb-4">This baby book was shared with you. You can leave it at any time.</p>
                    <button
                      onClick={leaveSharedBook}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                    >
                      Leave This Baby Book
                    </button>
                  </div>
                )}

                {/* Send Feedback */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4">Send Feedback</h3>
                  <p className="text-sm text-gray-600 mb-4">Help us improve BabyBookin! Share your suggestions, report issues, or let us know what you love.</p>
                  <p className="text-sm text-gray-500 mb-4">support@babybookin.com</p>
                  <a
                    href="mailto:support@babybookin.com?subject=BabyBookin%20Feedback"
                    className="bg-ocean-600 text-white px-4 py-2 rounded-lg hover:bg-ocean-700 inline-block"
                  >
                    Send Feedback
                  </a>
                </div>

                {/* Sign Out - NOT in Danger Zone */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4">Sign Out</h3>
                  <button
                    onClick={handleLogout}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                  >
                    Sign Out
                  </button>
                </div>

                {/* Danger Zone - Only Delete */}
                {currentBabyBook && currentBabyBook.ownerId === user?.uid && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-red-700">Danger Zone</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Deleting your baby book is permanent and cannot be undone. All entries, photos, and milestones will be permanently deleted.
                    </p>
                    <button
                      onClick={async () => {
                        setDeleteStep(1);
                        setDeletePassword('');
                        setDeleteError('');
                        setShowDeleteModal(true);
                      }}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                    >
                      Delete Baby Book
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {view === 'privacy' && (
          <div className="bg-white p-8 rounded-xl shadow-md max-w-4xl mx-auto">
            <button onClick={() => setView('home')} className="text-ocean-600 hover:text-ocean-800 mb-4">← Back</button>
            <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
            <p className="text-sm text-gray-500 mb-6">Last updated: January 2025</p>
            
            <div className="space-y-6 text-gray-700">
              <section>
                <h2 className="text-xl font-semibold mb-2">1. Information We Collect</h2>
                <p>When you use BabyBookin, we collect information you provide directly, including:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Account information (email address, name)</li>
                  <li>Baby book content (entries, photos, milestones, family information)</li>
                  <li>Payment information (processed securely through Stripe)</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">2. How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Provide and maintain the BabyBookin service</li>
                  <li>Send you daily reminders (if you opt in)</li>
                  <li>Process payments for premium features</li>
                  <li>Improve our services</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">3. Data Storage & Security</h2>
                <p>Your data is stored securely using Google Firebase with industry-standard encryption. Photos are stored in Firebase Storage. We implement appropriate security measures to protect your personal information.</p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">4. Sharing Your Information</h2>
                <p>We do not sell your personal information. We only share your data:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>With family members you explicitly invite to your baby book</li>
                  <li>With service providers (Firebase, Stripe, SendGrid) to operate our service</li>
                  <li>If required by law</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">5. Your Rights</h2>
                <p>You can:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Access and export your data at any time</li>
                  <li>Delete your account and all associated data</li>
                  <li>Opt out of daily reminders</li>
                  <li>Update your information in Settings</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">6. Children's Privacy</h2>
                <p>BabyBookin is intended for parents and family members to document their children's lives. We do not knowingly collect information directly from children under 13.</p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">7. Contact Us</h2>
                <p>If you have questions about this Privacy Policy, contact us at support@babybookin.com</p>
              </section>
            </div>
          </div>
        )}

        {view === 'terms' && (
          <div className="bg-white p-8 rounded-xl shadow-md max-w-4xl mx-auto">
            <button onClick={() => setView('home')} className="text-ocean-600 hover:text-ocean-800 mb-4">← Back</button>
            <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
            <p className="text-sm text-gray-500 mb-6">Last updated: January 2025</p>
            
            <div className="space-y-6 text-gray-700">
              <section>
                <h2 className="text-xl font-semibold mb-2">1. Acceptance of Terms</h2>
                <p>By accessing or using BabyBookin, you agree to be bound by these Terms of Service. If you do not agree, please do not use our service.</p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">2. Description of Service</h2>
                <p>BabyBookin is a digital baby book platform that allows you to document your baby's milestones, upload photos, and share memories with family members.</p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">3. User Accounts</h2>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>You must provide accurate information when creating an account</li>
                  <li>You are responsible for maintaining the security of your account</li>
                  <li>You must be at least 18 years old to create an account</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">4. User Content</h2>
                <p>You retain ownership of all content you upload to BabyBookin. By uploading content, you grant us a license to store, display, and process that content to provide our service. You are responsible for ensuring you have the right to upload any content.</p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">5. Acceptable Use</h2>
                <p>You agree not to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Upload illegal, harmful, or inappropriate content</li>
                  <li>Violate any laws or regulations</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Use the service for any commercial purpose without permission</li>
                </ul>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">6. Premium Subscriptions</h2>
                <p>Premium features are available through an auto-renewable subscription at $4.99/month. Payment will be charged to your Apple ID account at confirmation of purchase. Subscriptions automatically renew unless canceled at least 24 hours before the end of the current period. You can manage and cancel subscriptions in your Apple ID account settings. No cancellation of the current subscription is allowed during the active period.</p>





              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">7. Termination</h2>
                <p>We may suspend or terminate your account if you violate these terms. You may delete your account at any time through Settings.</p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">8. Disclaimer</h2>
                <p>BabyBookin is provided "as is" without warranties of any kind. We do our best to keep your data safe, but we are not liable for any data loss.</p>
              </section>
              
              <section>
                <h2 className="text-xl font-semibold mb-2">9. Contact</h2>
                <p>For questions about these Terms, contact us at support@babybookin.com</p>
              </section>
            </div>
          </div>
        )}

        {view === 'faq' && (
          <div className="bg-white p-8 rounded-xl shadow-md max-w-4xl mx-auto">
            <button onClick={() => setView('home')} className="text-ocean-600 hover:text-ocean-800 mb-4">← Back</button>
            <h1 className="text-3xl font-bold mb-6">Frequently Asked Questions</h1>
            
            <div className="space-y-6">
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-2">What is BabyBookin?</h3>
                <p className="text-gray-700">BabyBookin is a digital baby book that lets you capture and share your baby's precious moments, from pregnancy through their early years.</p>
              </div>
              
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-2">Is BabyBookin free?</h3>
                <p className="text-gray-700">Yes! The free plan includes up to 3 baby books, 150 photos per book, and 1 additional family member per book. Premium ($4.99/month) gives you unlimited photos, up to 6 baby books (additional books $1.99/mo each), and up to 6 people per book.</p>
              </div>
              
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-2">Can I invite family members?</h3>
                <p className="text-gray-700">Absolutely! Go to Settings and click "Invite User" to add grandparents, aunts, uncles, or anyone you want to share memories with. You can set them as viewers (can only view) or editors (can add entries).</p>
              </div>
              
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-2">How do I add photos?</h3>
                <p className="text-gray-700">When creating a new entry, you can upload multiple photos. You can also use the Bulk Upload feature on the home screen to upload many photos at once.</p>
              </div>
              
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-2">Can I print my baby book?</h3>
                <p className="text-gray-700">We're working on print partnerships! Soon you'll be able to export your book and have it professionally printed through our partners.</p>
              </div>
              
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-2">Is my data secure?</h3>
                <p className="text-gray-700">Yes. Your data is stored securely using Google Firebase with industry-standard encryption. Only you and the family members you invite can access your baby book.</p>
              </div>
              
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-2">How do I set up reminders?</h3>
                <p className="text-gray-700">Go to Settings → Daily Reminders and turn on the toggle. You'll get a daily push notification at 9 AM to remind you to capture new memories!</p>
              </div>
              
              <div className="pb-4">
                <h3 className="font-semibold text-lg mb-2">How do I contact support?</h3>
                <p className="text-gray-700">Email us at support@babybookin.com and we'll get back to you as soon as possible!</p>
              </div>
            </div>
          </div>
        )}
        {/* Footer */}
        {user && (
          <footer className="bg-white border-t mt-8 py-4">
            <div className="max-w-6xl mx-auto px-4 text-center">
              <div className="flex justify-center gap-6 text-sm text-gray-600">
                <button onClick={() => setView('privacy')} className="hover:text-ocean-600">Privacy Policy</button>
                <button onClick={() => setView('terms')} className="hover:text-ocean-600">Terms of Service</button>
                <button onClick={() => setView('faq')} className="hover:text-ocean-600">FAQ</button>
              </div>
              <p className="text-xs text-gray-500 mt-2">© 2026 BabyBookin. All rights reserved.</p>
            </div>
          </footer>
        )}
      </div> {/* This closes max-w-6xl mx-auto p-8 */}
    </div> {/* This closes min-h-screen */}


    {/* Print Book Modal */}
    <PrintBookFlow
      isOpen={showPrintBook}
      onClose={() => setShowPrintBook(false)}
      babyBook={currentBabyBook}
      entries={entries}
      milestones={milestones}
      userProfile={userProfile}
    />
    <ReviewModal 
      isOpen={reviewModalOpen}
      photos={selectedReviewPhotos}
      onClose={() => {
        setReviewModalOpen(false);
        setSelectedReviewPhotos([]);
        setAudioBase64(null);
      }}
      handleStartRecording={handleStartRecording}
      handleStopRecording={handleStopRecording}
      isRecording={isRecording}
      onSave={async (entryData) => {
        if (!babyBookId) {
          console.error('No baby book selected');
          return;
        }
        
        const result = await createEntry(babyBookId, {
          text: entryData.text,
          photoIds: entryData.photoIds,
          audioBase64: audioBase64,
          timestamp: entryData.timestamp,
          type: 'moment'
        });
        
        if (result.success) {
          const entriesResult = await getBabyBookEntries(babyBookId);
          if (entriesResult.success) {
            setEntries(entriesResult.entries);
          }
          console.log('✅ Moment saved to book!');
        }
      }}
    />
  </>
  );
}
